import { useState, useEffect } from "react";
import { Package, BarChart3, TrendingUp, TrendingDown, DollarSign, Clock, User } from "lucide-react";
import { ref, onValue, off } from "firebase/database";
import { database, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import StepsSection from "./StepsSection";
import LowStockTable from "./LowStockTable";

const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, "_");
  };

  const formatDateForComparison = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    let unsubscribeAuth;
    let productsListener;
    let salesListener;
    let purchasesListener;
    let receiptsListener;
    let invoicesListener;
    let transactionsListener;
    let productsRef;
    let salesRef;
    let purchasesRef;
    let receiptsRef;
    let invoicesRef;
    let transactionsRef;

    const fetchData = (user) => {
      if (!user) {
        setProducts([]);
        setSales([]);
        setPurchases([]);
        setReceipts([]);
        setInvoices([]);
        setTransactions([]);
        setLoading(false);
        return;
      }

      const encodedEmail = encodeEmailForPath(user.email);

      productsRef = ref(database, `users/${encodedEmail}/products`);
      productsListener = onValue(productsRef, (snapshot) => {
        const productsData = [];
        snapshot.forEach((childSnapshot) => {
          productsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        setProducts(productsData);
      });

      salesRef = ref(database, `users/${encodedEmail}/sales`);
      salesListener = onValue(salesRef, (snapshot) => {
        const salesData = [];
        snapshot.forEach((childSnapshot) => {
          salesData.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        setSales(salesData);
      });

      purchasesRef = ref(database, `users/${encodedEmail}/invoices`);
      purchasesListener = onValue(purchasesRef, (snapshot) => {
        const purchasesData = [];
        snapshot.forEach((childSnapshot) => {
          purchasesData.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        setPurchases(purchasesData);
      });

      receiptsRef = ref(database, `users/${encodedEmail}/receipts`);
      receiptsListener = onValue(receiptsRef, (snapshot) => {
        const receiptsData = [];
        snapshot.forEach((childSnapshot) => {
          receiptsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        setReceipts(receiptsData);
      });

      invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
      invoicesListener = onValue(invoicesRef, (snapshot) => {
        const invoicesData = [];
        snapshot.forEach((childSnapshot) => {
          invoicesData.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        setInvoices(invoicesData);
      });

      transactionsRef = ref(database, `users/${encodedEmail}/transactions`);
      transactionsListener = onValue(transactionsRef, (snapshot) => {
        const transactionsData = [];
        snapshot.forEach((childSnapshot) => {
          transactionsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        setTransactions(transactionsData);
        setLoading(false);
      });
    };

    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      fetchData(user);
    });

    return () => {
      if (productsListener) off(productsRef);
      if (salesListener) off(salesRef);
      if (purchasesListener) off(purchasesRef);
      if (receiptsListener) off(receiptsRef);
      if (invoicesListener) off(invoicesRef);
      if (transactionsListener) off(transactionsRef);
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, []);

  const getLowStockItems = () => {
    return products.filter(
      (item) => (Number.parseInt(item.openingStock) || 0) <= (Number.parseInt(item.lowStockAlert) || 0)
    );
  };

  const getSalesData = () => {
    const today = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return formatDateForComparison(date);
    }).reverse();

    const salesByDate = last7Days.map((date) => {
      const dailySales = sales.filter((sale) => {
        const saleDate = sale.date;
        const formattedSaleDate = formatDateForComparison(saleDate);
        return formattedSaleDate === date || saleDate === date;
      });

      const total = dailySales.reduce((sum, sale) => {
        const amount = Number.parseFloat(sale.totals?.netAmount) || Number.parseFloat(sale.netAmount) || 0;
        return sum + amount;
      }, 0);

      return total;
    });

    return salesByDate;
  };

  const getPurchaseData = () => {
    const today = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return date.toISOString().split("T")[0];
    }).reverse();

    const purchasesByDate = last7Days.map((date) => {
      const dailyPurchases = invoices.filter((invoice) => invoice.supplierDetails?.invoiceDate === date);
      const total = dailyPurchases.reduce((sum, invoice) => sum + (Number.parseFloat(invoice.summary?.netAmt) || 0), 0);
      return total;
    });

    return purchasesByDate;
  };

  const getTodaySalesMetrics = () => {
    const today = formatDateForComparison(new Date());
    const todaySales = sales.filter((sale) => {
      const saleDate = sale.date;
      const formattedSaleDate = formatDateForComparison(saleDate);
      return formattedSaleDate === today || saleDate === today;
    });

    const noOfSales = todaySales.length;
    const totalSales = todaySales.reduce((sum, sale) => {
      const amount = Number.parseFloat(sale.totals?.netAmount) || Number.parseFloat(sale.netAmount) || 0;
      return sum + amount;
    }, 0);

    return { noOfSales, totalSales };
  };

  const getLast30DaysSales = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const salesLast30Days = sales.filter((sale) => {
      const saleDate = new Date(sale.date.split('-').reverse().join('-')); // Convert DD-MM-YYYY to YYYY-MM-DD
      return saleDate >= thirtyDaysAgo && saleDate <= today;
    });

    const totalSalesLast30Days = salesLast30Days.reduce((sum, sale) => {
      const amount = Number.parseFloat(sale.totals?.netAmount) || Number.parseFloat(sale.netAmount) || 0;
      return sum + amount;
    }, 0);

    return totalSalesLast30Days;
  };

  const getTotalCustomers = () => {
    const uniqueCustomers = new Set(sales.map((sale) => sale.customer).filter((customer) => customer));
    return uniqueCustomers.size;
  };

  const getReceivablesData = () => {
    const today = new Date();
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return date.toISOString().split("T")[0];
    });

    return receipts
      .filter((receipt) => last30Days.includes(receipt.voucherDate))
      .map((receipt) => ({
        date: receipt.voucherDate,
        customer: receipt.customer || "Unknown",
        amount: Number.parseFloat(receipt.receiptAmount) || 0,
        outstanding: Number.parseFloat(receipt.netAmount) || 0,
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getPayablesData = () => {
    const today = new Date();
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return date.toISOString().split("T")[0];
    });

    return transactions
      .filter((transaction) => last30Days.includes(transaction.voucherDate))
      .map((transaction) => ({
        voucherNo: transaction.voucherNo || "N/A",
        date: transaction.voucherDate || "N/A",
        supplier: transaction.supplier || "Unknown",
        paymentAmount: Number.parseFloat(transaction.paymentAmount) || 0,
        outstanding: Number.parseFloat(transaction.netAmount) || 0,
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getReceivablesChartData = () => {
    const today = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return date.toISOString().split("T")[0];
    }).reverse();

    const receivablesByDate = last7Days.map((date) => {
      const dailyReceivables = receipts.filter((receipt) => receipt.voucherDate === date);
      const total = dailyReceivables.reduce((sum, receipt) => sum + (Number.parseFloat(receipt.receiptAmount) || 0), 0);
      return total;
    });

    return receivablesByDate;
  };

  const getPayablesChartData = () => {
    const today = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return date.toISOString().split("T")[0];
    }).reverse();

    const payablesByDate = last7Days.map((date) => {
      const dailyPayables = transactions.filter((transaction) => transaction.voucherDate === date);
      const total = dailyPayables.reduce(
        (sum, transaction) => sum + (Number.parseFloat(transaction.paymentAmount) || 0),
        0
      );
      return total;
    });

    return payablesByDate;
  };

  return {
    products,
    sales,
    purchases,
    receipts,
    invoices,
    transactions,
    loading,
    getLowStockItems,
    getSalesData,
    getPurchaseData,
    getTodaySalesMetrics,
    getLast30DaysSales,
    getTotalCustomers,
    getReceivablesData,
    getPayablesData,
    getReceivablesChartData,
    getPayablesChartData,
  };
};

const Dashboard = () => {
  const {
    loading,
    getLowStockItems,
    getSalesData,
    getPurchaseData,
    getTodaySalesMetrics,
    getLast30DaysSales,
    getTotalCustomers,
    getReceivablesData,
    getPayablesData,
    getReceivablesChartData,
    getPayablesChartData,
  } = useProducts();

  const [showLowStockTable, setShowLowStockTable] = useState(false);
  const [currentReceivablesPage, setCurrentReceivablesPage] = useState(1);
  const [currentPayablesPage, setCurrentPayablesPage] = useState(1);
  const itemsPerPage = 5;

  const lowStockItems = getLowStockItems();
  const salesData = getSalesData();
  const purchaseData = getPurchaseData();
  const { noOfSales, totalSales } = getTodaySalesMetrics();
  const totalSalesLast30Days = getLast30DaysSales();
  const totalCustomers = getTotalCustomers();
  const receivables = getReceivablesData();
  const payables = getPayablesData();
  const receivablesChartData = getReceivablesChartData();
  const payablesChartData = getPayablesChartData();

  // Calculate total receivables (To Receive) and payables (To Pay) for the last 30 days
  const totalReceivables = receivables.reduce((sum, receipt) => sum + (Number.parseFloat(receipt.amount) || 0), 0);
  const totalPayables = payables.reduce((sum, transaction) => sum + (Number.parseFloat(transaction.outstanding) || 0), 0);

  // Pagination for Receivables
  const totalReceivablesPages = Math.ceil(receivables.length / itemsPerPage);
  const paginatedReceivables = receivables.slice(
    (currentReceivablesPage - 1) * itemsPerPage,
    currentReceivablesPage * itemsPerPage
  );

  // Pagination for Payables
  const totalPayablesPages = Math.ceil(payables.length / itemsPerPage);
  const paginatedPayables = payables.slice(
    (currentPayablesPage - 1) * itemsPerPage,
    currentPayablesPage * itemsPerPage
  );

  const getTotalPaidLast30Days = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const paymentsLast30Days = payables.filter((payment) => {
      const paymentDate = new Date(payment.date);
      return paymentDate >= thirtyDaysAgo && paymentDate <= today;
    });

    const totalPaidLast30Days = paymentsLast30Days.reduce((sum, payment) => {
      return sum + (Number.parseFloat(payment.paymentAmount) || 0);
    }, 0);

    return totalPaidLast30Days;
  };

  const renderBarChart = (data, height, color, showAmounts = true) => {
    const maxVal = Math.max(...data, 1);
    const today = new Date();
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
    }).reverse();

    const generateYAxisLabels = (maxValue) => {
      const steps = 5;
      const stepValue = Math.ceil(maxValue / steps);
      return Array.from({ length: steps + 1 }, (_, i) => stepValue * i);
    };

    const yAxisLabels = generateYAxisLabels(maxVal);
    const chartHeight = height - 40; // Available height for bars (excluding padding)

    return (
      <div style={{ display: "flex", height: `${height}px`, position: "relative" }}>
        {showAmounts && (
          <div
            style={{
              display: "flex",
              flexDirection: "column-reverse",
              justifyContent: "space-between",
              height: `${chartHeight}px`,
              width: "60px",
              paddingRight: "10px",
              fontSize: "11px",
              color: "#666",
              marginTop: "10px",
            }}
          >
            {yAxisLabels.map((label, idx) => (
              <div key={idx} style={{ textAlign: "right", lineHeight: "1" }}>
                {label.toLocaleString()}
              </div>
            ))}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            height: `${height}px`,
            flex: 1,
            justifyContent: "space-around",
            padding: "10px 20px 30px 10px",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              right: "20px",
              bottom: "30px",
              backgroundImage: `repeating-linear-gradient(
                to top,
                transparent,
                transparent ${chartHeight / 5 - 1}px,
                #e5e7eb ${chartHeight / 5 - 1}px,
                #e5e7eb ${chartHeight / 5}px
              )`,
              pointerEvents: "none",
            }}
          />
          {data.map((val, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                position: "relative",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  height: `${Math.max((val / maxVal) * chartHeight, 2)}px`,
                  width: "32px",
                  background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                  borderRadius: "4px 4px 0 0",
                  transition: "all 0.3s ease",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  cursor: "pointer",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "scale(1.05)";
                  e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                  const tooltip = document.createElement("div");
                  tooltip.style.position = "absolute";
                  tooltip.style.background = "#d1e8f5";
                  tooltip.style.color = "#000";
                  tooltip.style.padding = "5px 10px";
                  tooltip.style.borderRadius = "4px";
                  tooltip.style.fontSize = "12px";
                  tooltip.style.fontWeight = "bold";
                  tooltip.style.zIndex = "1000";
                  tooltip.style.whiteSpace = "nowrap";
                  tooltip.innerHTML = `${days[idx]}<br/>₹${val.toLocaleString()}`;
                  tooltip.innerHTML +=
                    '<div style="position: absolute; width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 5px solid #d1e8f5; bottom: -5px; left: 50%; transform: translateX(-50%);"></div>';
                  tooltip.style.left = "50%";
                  tooltip.style.transform = "translateX(-50%) translateY(-100%)";
                  e.target.appendChild(tooltip);
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                  const tooltip = e.target.querySelector("div");
                  if (tooltip) e.target.removeChild(tooltip);
                }}
              />
              <span
                style={{
                  fontSize: "10px",
                  color: "#666",
                  transform: "rotate(-45deg)",
                  whiteSpace: "nowrap",
                  fontWeight: "500",
                }}
              >
                {days[idx]}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const styles = {
    container: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: "24px",
      marginTop: "-90px",
      minHeight: "100vh",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      padding: "20px 24px",
      borderRadius: "16px",
    },
    logo: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
    },
    logoIcon: {
      width: "48px",
      height: "48px",
      background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontWeight: "bold",
      boxShadow: "0 4px 8px rgba(255, 107, 53, 0.3)",
    },
    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: "24px",
      flex: 1,
      justifyContent: "flex-end",
    },
    navButtons: {
      display: "flex",
      gap: "12px",
    },
    navButton: {
      padding: "10px 18px",
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: "600",
      color: "white",
      border: "none",
      cursor: "pointer",
      transition: "all 0.3s ease",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      boxShadow: "0 4px 8px rgba(102, 126, 234, 0.3)",
    },
    lowStockIndicator: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "12px",
      cursor: "pointer",
      transition: "transform 0.3s ease",
      padding: "15px",
      borderRadius: "12px",
      background: "white",
    },
    gaugeContainer: {
      position: "relative",
      width: "100px",
      height: "60px",
    },
    gaugeSvg: {
      width: "100px",
      height: "60px",
      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
    },
    gaugeBackground: {
      fill: "none",
      stroke: "#e5e7eb",
      strokeWidth: "8",
      strokeLinecap: "round",
    },
    gaugeProgress: {
      fill: "none",
      stroke: lowStockItems.length > 0 ? "#10b981" : "#e5e7eb",
      strokeWidth: "8",
      strokeLinecap: "round",
      transition: "stroke-dashoffset 0.5s ease",
    },
    gaugeNumber: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      fontSize: "24px",
      fontWeight: "700",
      color: "#1f2937",
    },
    lowStockBadge: {
      fontSize: "12px",
      background: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)",
      color: "#3730a3",
      padding: "6px 14px",
      borderRadius: "20px",
      whiteSpace: "nowrap",
      fontWeight: "600",
      textAlign: "center",
      boxShadow: "0 2px 4px rgba(55, 48, 163, 0.2)",
    },
    stepsContainer: {
      flex: 3,
    },
    summaryCards: {
      display: "flex",
      gap: "24px",
      margin: "24px 0",
    },
    card: {
      background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
      padding: "24px",
      borderRadius: "16px",
      flex: 1,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      cursor: "pointer",
    },
    chartsContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "24px",
      marginBottom: "24px",
    },
    chartRow: {
      display: "flex",
      gap: "24px",
    },
    chartCard: {
      background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
      padding: "24px",
      borderRadius: "16px",
      flex: 1,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      transition: "transform 0.3s ease",
    },
    chartTitle: {
      fontSize: "18px",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontWeight: "700",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },
    tablesContainer: {
      display: "flex",
      gap: "24px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "16px",
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    },
    th: {
      background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
      padding: "14px 16px",
      textAlign: "left",
      fontSize: "13px",
      fontWeight: "700",
      color: "#374151",
      borderBottom: "2px solid #e2e8f0",
    },
    td: {
      padding: "14px 16px",
      borderBottom: "1px solid #f1f5f9",
      fontSize: "13px",
      color: "#374151",
      backgroundColor: "#ffffff",
    },
    footer: {
      marginTop: "32px",
      padding: "20px",
      background: "linear-gradient(135deg, #343a40 0%, #495057 100%)",
      color: "white",
      borderRadius: "12px",
      fontSize: "13px",
      textAlign: "center",
      position: "relative",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    },
    footerLogo: {
      position: "absolute",
      right: "20px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "24px",
      height: "24px",
      background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
      borderRadius: "4px",
    },
    paginationContainer: {
      display: "flex",
      justifyContent: "center",
      gap: "8px",
      marginTop: "16px",
    },
    paginationButton: {
      padding: "8px 16px",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
      fontSize: "12px",
      fontWeight: "600",
      color: "#374151",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    paginationButtonActive: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "white",
      border: "none",
    },
    paginationButtonDisabled: {
      background: "#f1f5f9",
      color: "#9ca3af",
      cursor: "not-allowed",
    },
  };

  if (loading) {
    return (
      <div
        style={{
          ...styles.container,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid rgba(255, 255, 255, 0.3)",
              borderTop: "4px solid #ffffff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          ></div>
          <p style={{ color: "white", fontSize: "16px", fontWeight: "500" }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.logo}></div>
        <div style={styles.headerRight}>
          <div style={styles.stepsContainer}>
            <StepsSection />
          </div>
          <div
            style={styles.lowStockIndicator}
            onClick={() => setShowLowStockTable(true)}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <div style={styles.gaugeContainer}>
              <svg style={styles.gaugeSvg} viewBox="0 0 100 60">
                <path d="M 10 50 A 40 40 0 0 1 90 50" style={styles.gaugeBackground} />
                <path
                  d="M 10 50 A 40 40 0 0 1 90 50"
                  style={{
                    ...styles.gaugeProgress,
                    strokeDasharray: "125.6",
                    strokeDashoffset: lowStockItems.length > 0 ? "62.8" : "125.6",
                  }}
                />
              </svg>
              <div style={styles.gaugeNumber}>{lowStockItems.length}</div>
            </div>
            <div style={styles.lowStockBadge}>Low Stock Items : {lowStockItems.length}</div>
          </div>
        </div>
      </div>

      <div style={styles.summaryCards}>
        <div
          style={styles.card}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 20px 40px -10px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              fontSize: "15px",
              color: "#6b7280",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: "600",
            }}
          >
            <Clock size={18} color="#667eea" />
            Date: {new Date().toLocaleDateString("en-GB")}
          </div>
          <div style={{ fontSize: "15px", color: "#6b7280", marginBottom: "8px", fontWeight: "500" }}>
            No of Sales: <span style={{ color: "#1f2937", fontWeight: "700" }}>{noOfSales}</span>
          </div>
          <div
            style={{
              fontSize: "15px",
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: "500",
            }}
          >
            Total Sales: <span style={{ color: "#059669", fontWeight: "700" }}>₹{totalSales.toFixed(2)}</span>
          </div>
        </div>

        <div
          style={styles.card}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 20px 40px -10px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div style={{ fontSize: "15px", color: "#6b7280", marginBottom: "8px", fontWeight: "500" }}>
            Last 30 days: <span style={{ color: "#1f2937", fontWeight: "700" }}>₹{totalSalesLast30Days.toFixed(2)}</span>
          </div>
          <div
            style={{
              fontSize: "15px",
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: "500",
            }}
          >
            Total Customer: <span style={{ color: "#059669", fontWeight: "700" }}>{totalCustomers}</span>
            <User size={18} color="#059669" />
          </div>
        </div>

        <div
          style={styles.card}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 20px 40px -10px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              fontSize: "15px",
              color: "#059669",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "8px",
            }}
          >
            <TrendingDown size={18} />
            To Receive: ₹{totalReceivables.toFixed(2)}
          </div>
          <div
            style={{
              fontSize: "15px",
              color: "#dc2626",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <TrendingUp size={18} />
            To Pay: ₹{getTotalPaidLast30Days().toFixed(2)}
          </div>
        </div>
      </div>

      <div style={styles.chartsContainer}>
        <div style={styles.chartRow}>
          <div style={styles.chartCard}>
            <h3 style={{ ...styles.chartTitle }}>
              <BarChart3 size={20} />
              Sales Report
            </h3>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>Sales status</div>
            <div style={{ height: "200px" }}>{renderBarChart(salesData, 200, "#5bc0de")}</div>
            <p style={{ textAlign: "center", fontSize: "12px", color: "#666", marginTop: "10px" }}>
              (Last 7 days Report)
            </p>
          </div>
          <div style={styles.chartCard}>
            <h3 style={{ ...styles.chartTitle }}>
              <TrendingUp size={20} />
              Purchase Report
            </h3>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>Purchase status</div>
            <div style={{ height: "200px" }}>{renderBarChart(purchaseData, 200, "#28a745")}</div>
            <p style={{ textAlign: "center", fontSize: "12px", color: "#666", marginTop: "10px" }}>
              (Last 7 days Report)
            </p>
          </div>
        </div>

        <div style={styles.chartRow}>
          <div style={styles.chartCard}>
            <h3 style={{ ...styles.chartTitle }}>
              <DollarSign size={20} />
              Receivables Report
            </h3>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>Receivables status</div>
            <div style={{ height: "200px" }}>{renderBarChart(receivablesChartData, 200, "#28a745")}</div>
            <p style={{ textAlign: "center", fontSize: "12px", color: "#666", marginTop: "10px" }}>
              (Last 7 days Report)
            </p>
          </div>
          <div style={styles.chartCard}>
            <h3 style={{ ...styles.chartTitle }}>
              <Package size={20} />
              Payables Report
            </h3>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>Payables status</div>
            <div style={{ height: "200px" }}>{renderBarChart(payablesChartData, 200, "#dc3545")}</div>
            <p style={{ textAlign: "center", fontSize: "12px", color: "#666", marginTop: "10px" }}>
              (Last 7 days Report)
            </p>
          </div>
        </div>
      </div>

      <div style={styles.tablesContainer}>
        <div style={styles.chartCard}>
          <h3 style={{ ...styles.chartTitle }}>
            <DollarSign size={20} />
            Receivables
          </h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>S.No</th>
                <th style={styles.th}>Due Date</th>
                <th style={styles.th}>Customer Name</th>
                <th style={styles.th}>Receipt Amount</th>
                <th style={styles.th}>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReceivables.length > 0 ? (
                paginatedReceivables.map((item, idx) => (
                  <tr key={idx}>
                    <td style={styles.td}>{(currentReceivablesPage - 1) * itemsPerPage + idx + 1}</td>
                    <td style={styles.td}>{item.date}</td>
                    <td style={styles.td}>{item.customer}</td>
                    <td style={styles.td}>₹{item.amount.toFixed(2)}</td>
                    <td style={styles.td}>₹{item.outstanding.toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ ...styles.td, textAlign: "center" }}>
                    No receivables found for the last 30 days
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div style={styles.paginationContainer}>
            {Array.from({ length: totalReceivablesPages }, (_, i) => (
              <button
                key={i + 1}
                style={{
                  ...styles.paginationButton,
                  ...(currentReceivablesPage === i + 1 ? styles.paginationButtonActive : {}),
                }}
                onClick={() => setCurrentReceivablesPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <div style={{ marginTop: "10px", fontSize: "12px", color: "#666" }}>
            Showing {paginatedReceivables.length} of {receivables.length} entries
          </div>
        </div>

        <div style={styles.chartCard}>
          <h3 style={{ ...styles.chartTitle }}>
            <Package size={20} />
            Payables
          </h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>S.No</th>
                <th style={styles.th}>Voucher No</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Supplier</th>
                <th style={styles.th}>Payment Amount</th>
                <th style={styles.th}>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPayables.length > 0 ? (
                paginatedPayables.map((item, idx) => (
                  <tr key={idx}>
                    <td style={styles.td}>{(currentPayablesPage - 1) * itemsPerPage + idx + 1}</td>
                    <td style={styles.td}>{item.voucherNo}</td>
                    <td style={styles.td}>{item.date}</td>
                    <td style={styles.td}>{item.supplier}</td>
                    <td style={styles.td}>₹{item.paymentAmount.toFixed(2)}</td>
                    <td style={styles.td}>₹{item.outstanding.toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ ...styles.td, textAlign: "center" }}>
                    No payables found for the last 30 days
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div style={styles.paginationContainer}>
            {Array.from({ length: totalPayablesPages }, (_, i) => (
              <button
                key={i + 1}
                style={{
                  ...styles.paginationButton,
                  ...(currentPayablesPage === i + 1 ? styles.paginationButtonActive : {}),
                }}
                onClick={() => setCurrentPayablesPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <div style={{ marginTop: "10px", fontSize: "12px", color: "#666" }}>
            Showing {paginatedPayables.length} of {payables.length} entries
          </div>
        </div>
      </div>

      {showLowStockTable && <LowStockTable lowStockItems={lowStockItems} onClose={() => setShowLowStockTable(false)} />}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .summaryCards, .chartsContainer .chartRow, .tablesContainer {
            flex-direction: column;
          }
          .header {
            flex-direction: column;
            gap: 16px;
          }
          .headerRight {
            flex-direction: column;
            alignItems: center;
            width: 100%;
          }
          .stepsContainer {
            width: 100%;
          }
          .lowStockIndicator {
            marginTop: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;